import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, ShieldCheck, HelpCircle, CheckCircle, AlertTriangle, Trophy } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import { salaryNegotiationBank } from '../../../data/SalaryNegotiationBank';
import { withKinematicSensors } from '../../../core/hocs/withKinematicSensors';

function NegotiationGameBase({ gameData, onComplete, resetKinematics, getKinematicMetrics, startHover, endHover }) {
  const { playSuccess, playError, playExtinguish, playWin } = useGameSounds();
  const { id = 'game_salary_negotiation', preGameBriefing } = gameData || {};

  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
      let array = [...salaryNegotiationBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 3);
  }

  const activeDeck = deckRef.current;

  const [stage, setStage] = useState('briefing');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [feedback, setFeedback] = useState(null);
  
  const [sessionAttempts, setSessionAttempts] = useState([]);

  const activeScenario = activeDeck[currentIndex];

  const shuffledOptions = useMemo(() => {
      if (!activeScenario) return [];
      return [...activeScenario.options].sort(() => Math.random() - 0.5);
  }, [activeScenario]);

  useEffect(() => {
      if (preGameBriefing && currentIndex === 0 && stage === 'briefing') {
          setStage('briefing');
      } else if (stage !== 'results' && stage !== 'intervention') {
          setStage('playing');
          resetKinematics(); 
      }
  }, [preGameBriefing, currentIndex, stage, resetKinematics]);

  const triggerFailure = (option) => {
      playExtinguish();
      const logic = {
          title: "Negotiation Breakdown",
          script: option.feedback || "You deployed an aggressive or sub-optimal negotiation tactic. You must separate emotion from financial leverage.",
          videoUrl: null
      };
      setActiveIntervention(logic);
      setStage('intervention');
  };

  const handleMouseEnter = (category) => {
      if (category !== 'Optimal') startHover(category);
  };

  const handleMouseLeave = (category) => {
      if (category !== 'Optimal') endHover(category);
  };

  const handleOptionSelect = (option) => {
    if (stage !== 'playing') return;
    
    endHover(option.category);
    const metrics = getKinematicMetrics();
    const hoverSums = metrics.hoverDurationsMs ? Object.values(metrics.hoverDurationsMs).reduce((a,b)=>a+b,0) : 0;

    const interactionRecord = {
        scenarioId: activeScenario.id,
        selectedCategory: option.category,
        isCorrect: option.category === 'Optimal',
        volatilityHoverMs: hoverSums,
        timestamp: Date.now()
    };

    setSessionAttempts(prev => [...prev, interactionRecord]);
    resetKinematics(); 

    if (option.category === 'Optimal') {
        playSuccess();
        setFeedback({ type: 'success', text: option.feedback });
        
        setTimeout(() => {
            setFeedback(null);
            if (currentIndex + 1 < activeDeck.length) {
                setCurrentIndex(i => i + 1);
            } else {
                playWin();
                setStage('results');
            }
        }, 1500);

    } else {
        playError();
        triggerFailure(option);
    }
  };

  const handleRetry = () => {
      let array = [...salaryNegotiationBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 3);

      setCurrentIndex(0);
      setActiveIntervention(null);
      setFeedback(null);
      setStage('playing');
      resetKinematics();
  };

  const handleFinalSubmit = () => {
      const payload = {
          id: gameData?.id || 'game_salary_negotiation',
          rawInteractions: sessionAttempts
      };

      if (typeof onComplete === 'function') {
          onComplete(payload);
      }
  };

  if (stage === 'results') {
      return (
        <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-xl p-8 text-center animate-fade-in">
          <div className="mb-6 p-6 rounded-full bg-emerald-100">
             <Trophy className="w-16 h-16 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-2 uppercase tracking-tight">Total Rewards Maximized</h2>
          <p className="text-slate-600 mb-8 max-w-sm">
             You consistently negotiated using objective data and strategic leverage, avoiding emotional ultimatums.
          </p>
          <button
            onClick={handleFinalSubmit} 
            className="w-full max-w-xs bg-emerald-600 text-white font-black py-4 px-6 rounded-xl shadow-lg hover:bg-emerald-700 transition transform hover:scale-105 flex items-center justify-center gap-2 uppercase tracking-widest"
          >
            <CheckCircle className="w-5 h-5" /> Complete Module
          </button>
        </div>
      );
  }

  return (
    <div className="flex flex-col h-full bg-slate-100 rounded-xl overflow-hidden border border-slate-300 shadow-sm relative min-h-[600px] font-sans">
      
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay
              type="briefing"
              data={preGameBriefing}
              onContinue={() => setStage('playing')}
            />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="intervention" 
                    data={activeIntervention} 
                    onRetry={handleRetry} 
                />
            </div>
        )}
      </AnimatePresence>

      {(stage === 'playing' || stage === 'results') && (
        <div className="p-4 border-b bg-white flex justify-between items-center z-10 shadow-sm w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-xl">👤</div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">Recruiter (AI)</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Offer</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
             <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-bold mr-2">
                  Scenario {currentIndex + 1}/{activeDeck.length}
             </span>
          </div>
        </div>
      )}

      {stage === 'playing' && (
        <div className="p-4 md:p-8 flex-grow overflow-y-auto flex flex-col gap-6">
           
           <div className="self-center max-w-[90%] bg-slate-200 border border-slate-300 text-slate-700 p-3 rounded-xl text-xs font-bold uppercase tracking-widest shadow-sm text-center">
              Context: {activeScenario?.scenarioContext}
           </div>

           <div className="self-start max-w-[85%] bg-white p-4 rounded-2xl rounded-tl-none border border-slate-200 shadow-sm text-sm font-medium text-slate-800 leading-relaxed">
              {activeScenario?.recruiterPrompt}
           </div>

           <div className="w-full mt-auto space-y-3">
               <AnimatePresence mode="wait">
                   <motion.div key={activeScenario?.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 flex flex-col items-end">
                       {shuffledOptions.map((option, idx) => (
                           <button
                               key={idx}
                               disabled={!!feedback}
                               onClick={() => handleOptionSelect(option)}
                               onMouseEnter={() => handleMouseEnter(option.category)}
                               onMouseLeave={() => handleMouseLeave(option.category)}
                               className="w-full md:max-w-[90%] text-left p-4 bg-indigo-50 border-2 border-indigo-100 text-indigo-900 rounded-2xl rounded-tr-none hover:bg-indigo-600 hover:text-white hover:border-indigo-600 hover:shadow-md transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                               <span className="font-medium leading-relaxed text-sm">
                                   {option.responseText}
                               </span>
                           </button>
                       ))}
                   </motion.div>
               </AnimatePresence>
           </div>
           
           <AnimatePresence>
              {feedback && feedback.type === 'success' && (
                  <motion.div 
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="absolute bottom-0 left-0 right-0 p-5 bg-emerald-50 border-t-4 border-emerald-500 flex items-start gap-3 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-50"
                  >
                      <CheckCircle className="text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                          <h4 className="font-black text-xs text-emerald-800 uppercase tracking-widest mb-1">Leverage Executed</h4>
                          <p className="text-sm text-emerald-900 font-medium">{feedback.text}</p>
                      </div>
                  </motion.div>
              )}
           </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default withKinematicSensors(NegotiationGameBase);